import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { clientEnv } from "../env";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { verifiedFactorId, verifiedFactorKey } from "./mfaEnrollment";
import { withTemporaryPasswordSession, type ReauthFailure } from "./reauthenticate";

/**
 * The browser half of the step-up grant, which is the mechanism plan.md section 8 requires in front
 * of a sensitive action.
 *
 * The server half already exists and nothing here reimplements it.
 * `supabase/migrations/20260902200001_phase5b_step_up_wrapper_and_secret_consumption.sql` created
 * `public.request_step_up_grant`, and
 * `20260905160001_phase5e_step_up_wraps_the_actions_that_already_consume_it.sql` replaced it with the
 * two-branch version described under the divider below. Both issue the grant through
 * `private.issue_step_up_grant`, and the gateway that performs the protected action consumes it in
 * the same transaction. `scripts/staging-mfa.mjs` does what the TOTP path here does, and the staging
 * suite runs it against the hosted project.
 *
 * For that path the wrapper checks that the session is `aal2` and that the access token carries a
 * `totp` entry in its `amr` claim no older than 300 seconds. Three properties of that decide the
 * shape below.
 *
 * **The client generates the token and the database only ever sees its SHA-256.** The value returned
 * here is what must reach the protected gateway; `private.consume_step_up_grant` hashes what it is
 * given and compares. So the raw token is never sent anywhere except to the gateway that spends it.
 *
 * **Freshness is proved by answering the factor, not by asking nicely.** A challenge and a verify
 * against the account's own TOTP factor is what puts a new `totp` entry in the token, and that
 * timestamp is what the wrapper measures. The same two-step exchange `VerificationRoute` and
 * `mfaEnrollment.confirmEnrollment` use, because it is the same GoTrue mechanism.
 *
 * **The grant lives 300 seconds and so does the freshness window behind it.** Everything from the
 * code being typed to the gateway being called has to happen inside that, which is why this function
 * takes the code and returns a token rather than exposing the two halves separately.
 *
 * ---
 *
 * **The wrapper has two branches since migration
 * `20260905160001_phase5e_step_up_wraps_the_actions_that_already_consume_it.sql`, and this file has
 * one function for each.** That migration replaced the allow-list the paragraphs above were written
 * against. It holds four actions now: the two one-time-secret gateways, `management_cancellation`
 * and `membership_change`.
 *
 * What forced the widening is plan.md 5.12 step 1, "Re-authenticate".
 * `public.request_management_cancellation` has consumed a grant since migration 3 and no browser
 * could obtain one, so the end-management flow was reachable only from a test that issues grants as
 * `app_admin_owner`. `PORTAL_STEP_UP_ACTION_NOT_WRAPPED` is still raised for anything outside those
 * four and `action-not-wrapped` below still renders it; what changed is that a cancellation no
 * longer lands there.
 *
 * **`management_cancellation` left again on 2026-10-07.** The client cancels by mail or in the chat
 * and Petrovic records it, so the client's cancellation gateways were dropped and
 * `20261007100001` took the action off the wrapper's list. A grant for it is refused as
 * `PORTAL_STEP_UP_ACTION_NOT_WRAPPED`, like any action nothing would accept.
 *
 * The second branch is plan.md 14.3 as written: "A step-up endpoint re-verifies **password or
 * TOTP**", with plan.md section 4, "Optional TOTP MFA for client users", making a client's TOTP
 * optional in the first place. A caller who holds no verified factor, asking for an action that is
 * not secret viewing, proves themselves with a `password` entry in the `amr` claim at most 300
 * seconds old. A caller who does hold a verified factor may not use that branch: on an `aal1`
 * session they are refused with `PORTAL_STEP_UP_AAL2_REQUIRED`, because otherwise enrolling TOTP
 * would lower the bar rather than raise it.
 *
 * So `requestStepUpGrant` is the TOTP path, unchanged, and `requestStepUpGrantWithPassword` is the
 * password path. They are not interchangeable and the screen picks by what the account actually has:
 * a verified factor gets the code field, no verified factor gets the password field.
 *
 * The password path is the only reason `withTemporaryPasswordSession` exists as its own export. The
 * freshness the wrapper measures is in the token of a session created seconds ago, so the grant has
 * to be requested with **that** token rather than with the primary session's, whose own `password`
 * entry is hours old and is refused as `PORTAL_STEP_UP_PASSWORD_STALE`. architecture.md section 4
 * fixes the rest of the exchange, including the finding from the staging spike of 2026-08-30 that
 * CAPTCHA protection applies to `grant_type=password` itself and not only to the first sign-in: the
 * surface offering the password field renders a Turnstile widget and passes its token, exactly as
 * the sign-in form does.
 *
 * Secret viewing stays TOTP for everybody, whatever the account holds. architecture.md section 4:
 * "Secret viewing always uses the fresh TOTP path; password-only users are refused."
 */

/**
 * The action this portal asks a grant for.
 *
 * **All four, as of 2026-09-12.** This union held one member and its own comment said it "is widened
 * when a screen that asks for one of the other three exists, and not before". All three of those
 * screens were built in the same round, so the union now matches `public.request_step_up_grant`'s
 * allow-list exactly rather than being deliberately narrower than it. That is not a licence to skip
 * the argument next time: a fifth name belongs here only when a fifth screen does.
 *
 * The two branches behind them are not the same, and the difference is the database's decision
 * rather than this file's. `20260905160001` sets `v_method := 'totp'` for `create_one_time_secret`
 * and `consume_one_time_secret` **before** it looks at whether the caller holds a factor, then
 * refuses an `aal1` session. So a password-only account cannot send or open a secret at all, which
 * plan.md section 5.10 states for the recipient and not for the sender; `/secrets` asks
 * `useVerifiedFactor` and says so before it offers a field, because finding that out after typing a
 * secret into a form is worse than being told first. `membership_change` takes the password branch
 * when the caller holds no verified factor, which is plan.md 14.3 as written and what keeps a
 * client's TOTP optional. `management_cancellation` was the other member that took it, until
 * 2026-10-07, when the screen that asked for it was removed with the client's cancellation.
 */
export type StepUpAction =
  | "membership_change"
  | "create_one_time_secret"
  | "consume_one_time_secret"
  /**
   * Added 2026-09-15 with the one screen that asks for it, `app/routes/AdminClientErasurePanel.tsx`.
   * `20260915000006` makes it TOTP for everybody and refuses anybody who is not a Petrovic admin, so
   * the password branch can never produce one.
   */
  | "tenant_erasure"
  /**
   * Setting one client file aside, `public.admin_quarantine_attachment`. `20260915000007` makes it
   * TOTP for everybody and Petrovic's alone, exactly as `tenant_erasure` is.
   */
  | "attachment_quarantine"
  /**
   * Added 2026-09-22 with the one screen that asks for it, Ändra e-postadress in
   * `app/routes/AdminClientPeoplePanel.tsx`, spent by `public.admin_change_contact_email`.
   * `20260922200001` makes it TOTP for everybody, requires a tenant and refuses anybody who is not a
   * Petrovic admin, exactly as the two above, because the address of a contact with an account is
   * the address that account signs in and recovers its password through.
   */
  | "contact_email_change";

export type StepUpFailure =
  | "code-wrong"
  | "code-expired"
  | "challenge-unavailable"
  | "action-not-wrapped"
  | "aal2-required"
  | "totp-required"
  | "totp-stale"
  | "password-wrong"
  | "password-required"
  | "password-stale"
  | "different-account"
  | "captcha"
  | "tenant-required"
  | "rate-limited"
  | "unavailable";

/**
 * Every refusal gets its own sentence, because they ask the reader for different things.
 *
 * A wrong code is retyped. An aged-out verification is redone. An action the database does not wrap
 * is nobody's to fix from this screen, and the sentence says so instead of inviting a retry that
 * will refuse again. Each one says what happened, that nothing was changed, and what the person does
 * next, because every caller of this list is standing in front of an action that either happened or
 * did not.
 *
 * **`no-factor` was removed rather than reworded, and it stays removed.** It said a cancellation
 * requires two-factor login and pointed at the panel that switches it on. That is false for a
 * cancellation: migration `20260905160001` gives a client with no verified factor the password
 * branch, and plan.md section 4, "Optional TOTP MFA for client users", keeps a client's TOTP
 * optional.
 *
 * It is true for the two secret actions, which joined `StepUpAction` on 2026-09-12, and it still has
 * no home here. `/secrets` never reaches this list for that case, because the screen asks
 * `useVerifiedFactor` and says why before it offers a field, which is a better answer than a refusal
 * after somebody has typed a secret into a form. The server's own refusal,
 * `PORTAL_STEP_UP_TOTP_REQUIRED`, is the backstop underneath that and it is the database's sentence
 * rather than this file's.
 */
export const STEP_UP_FAILURE_TEXT: Record<StepUpFailure, string> = {
  "code-wrong":
    "Koden stämmer inte. Kontrollera att appen visar koden för det här kontot, och att telefonens klocka går rätt.",
  "code-expired": "Koden hann gå ut. Ange koden som appen visar nu.",
  "challenge-unavailable":
    "Vi kunde inte hämta någon kontroll från servern. Ingenting har ändrats. Försök igen.",
  "action-not-wrapped":
    "Portalen kan inte styrka en uppsägning ännu. Ingenting är uppsagt och ingenting har ändrats. Skriv i chatten så tar vi uppsägningen den vägen.",
  "aal2-required": "Inloggningen är inte styrkt med en kod. Logga in igen och ange koden från appen.",
  // Reached only by an account that has a verifieringsapp, since a caller without one is sent to the
  // password branch by the database itself. The old sentence said the account had no app registered,
  // which is now the one thing this refusal cannot mean.
  "totp-required":
    "Vi kunde inte se någon kod från din verifieringsapp i den här inloggningen. Ingenting har ändrats. Logga in igen, ange koden från appen och försök på nytt.",
  "totp-stale":
    "Det tog för lång tid mellan koden och bekräftelsen. Ange koden som appen visar nu och bekräfta igen.",
  "password-wrong":
    "Lösenordet stämmer inte. Ingenting har ändrats. Skriv lösenordet du loggar in med och försök igen.",
  "password-required":
    "Vi kunde inte se att du just skrev ditt lösenord. Ingenting har ändrats. Skriv lösenordet igen och bekräfta.",
  "password-stale":
    "Det tog för lång tid mellan lösenordet och bekräftelsen. Ingenting har ändrats. Skriv lösenordet igen och bekräfta direkt.",
  "different-account":
    "Uppgifterna hör till ett annat konto. Ingenting har ändrats. Logga in igen och försök på nytt.",
  captcha:
    "Säkerhetskontrollen gick inte igenom. Ingenting har ändrats. Ladda om sidan och försök igen.",
  "tenant-required":
    "Vi kunde inte se vilket kundkonto det gäller. Ingenting har ändrats. Ladda om sidan och försök igen.",
  "rate-limited": "För många försök på kort tid. Vänta en stund och försök igen.",
  unavailable:
    "Vi kunde inte styrka din identitet just nu. Ingenting har ändrats. Försök igen."
};

export type StepUpResult = { ok: true; token: string } | { ok: false; reason: StepUpFailure };

/**
 * The factor a step-up would be proved with, or null when the account has none.
 *
 * A screen has to know this before it offers a code field: somebody with no verified factor has no
 * code to type, and finding that out after typing six digits is worse than being told first. Keyed
 * on `verifiedFactorKey`, so the factor panel on the same page and this share one answer and one
 * request.
 */
export function useVerifiedFactor(): UseQueryResult<string | null> {
  return useQuery({ queryKey: verifiedFactorKey, queryFn: verifiedFactorId });
}

/** GoTrue's own error shapes, read the way `VerificationRoute` reads them. */
function classifyVerify(error: { code?: string; message?: string } | null): StepUpFailure {
  const code = error?.code ?? "";
  const message = (error?.message ?? "").toLowerCase();

  if (code === "mfa_challenge_expired" || message.includes("expired")) {
    return "code-expired";
  }
  if (code === "mfa_verification_failed" || message.includes("invalid")) {
    return "code-wrong";
  }
  return "unavailable";
}

/**
 * The wrapper's own machine codes, each mapped to the thing it actually means.
 *
 * Anything unrecognised becomes `unavailable` rather than the nearest named refusal. Telling
 * somebody their code was too old because the server returned something this file has never seen is
 * a failure rendered as a fact.
 */
function classifyGrant(error: { message?: string } | null): StepUpFailure {
  const raw = error?.message ?? "";

  if (raw.includes("PORTAL_STEP_UP_ACTION_NOT_WRAPPED")) {
    return "action-not-wrapped";
  }
  if (raw.includes("PORTAL_STEP_UP_AAL2_REQUIRED")) {
    return "aal2-required";
  }
  if (raw.includes("PORTAL_STEP_UP_TOTP_REQUIRED")) {
    return "totp-required";
  }
  if (raw.includes("PORTAL_STEP_UP_TOTP_STALE")) {
    return "totp-stale";
  }
  if (raw.includes("PORTAL_STEP_UP_PASSWORD_REQUIRED")) {
    return "password-required";
  }
  if (raw.includes("PORTAL_STEP_UP_PASSWORD_STALE")) {
    return "password-stale";
  }
  if (raw.includes("PORTAL_STEP_UP_TENANT_REQUIRED")) {
    return "tenant-required";
  }
  if (raw.includes("PORTAL_RATE_LIMITED")) {
    return "rate-limited";
  }
  return "unavailable";
}

/**
 * GoTrue's refusals, renamed into this file's vocabulary and no further.
 *
 * The mapping exists because the two files answer different questions. `reauthenticate.ts` reports
 * on a password check; this file reports on a step-up that either produced a grant or did not. Every
 * member is carried across rather than collapsed into `unavailable`, because a wrong password, an
 * unanswered captcha and somebody else's account ask the person to do three different things.
 */
const REAUTH_AS_STEP_UP: Record<ReauthFailure, StepUpFailure> = {
  "wrong-password": "password-wrong",
  "different-account": "different-account",
  captcha: "captcha",
  "rate-limited": "rate-limited",
  unavailable: "unavailable"
};

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * The token the caller keeps and the hash the database is told about.
 *
 * Both paths mint it the same way and neither sends the value anywhere except to the gateway that
 * spends it. `p_grant_token_hash` is checked against `^[0-9a-f]{64}$` by the wrapper, which is what
 * a SHA-256 in this encoding is.
 */
async function mintGrantToken(): Promise<{ token: string; hash: string }> {
  const token = toHex(crypto.getRandomValues(new Uint8Array(32)));
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(token));

  return { token, hash: toHex(new Uint8Array(digest)) };
}

/**
 * One grant, proved with one code, in the shape the database expects.
 *
 * `factorId` is the account's verified TOTP factor, read by `verifiedFactorId`. It is a parameter
 * rather than something read here, because the screen has to know which branch the account is on
 * before it offers any field at all: somebody with no factor has no code to type, and finding that
 * out after typing six digits is worse than being asked for the password instead.
 */
export async function requestStepUpGrant(input: {
  factorId: string;
  code: string;
  action: StepUpAction;
  tenantId: string;
}): Promise<StepUpResult> {
  const supabase = getSupabaseBrowserClient();

  const challenge = await supabase.auth.mfa.challenge({ factorId: input.factorId });
  if (challenge.error || !challenge.data) {
    return {
      ok: false,
      reason: challenge.error ? classifyVerify(challenge.error) : "challenge-unavailable"
    };
  }

  // `verify` issues a new access token carrying a fresh `totp` entry, and the client persists it.
  // That token is what the grant request below is authorised with, which is the whole point of the
  // exchange: the freshness the wrapper measures is created here and nowhere else.
  const verified = await supabase.auth.mfa.verify({
    factorId: input.factorId,
    challengeId: challenge.data.id,
    code: input.code.trim()
  });

  if (verified.error) {
    return { ok: false, reason: classifyVerify(verified.error) };
  }

  const { token, hash } = await mintGrantToken();

  const { error } = await supabase.rpc("request_step_up_grant", {
    p_intended_action: input.action,
    p_grant_token_hash: hash,
    p_tenant_id: input.tenantId
  });

  if (error) {
    return { ok: false, reason: classifyGrant(error) };
  }

  return { ok: true, token };
}

/**
 * The same RPC, called with a token the Supabase client does not hold.
 *
 * A plain `fetch` rather than a second client. `getSupabaseBrowserClient` is a singleton whose own
 * comment forbids a second stateful instance: two clients would install two auth listeners and two
 * refresh timers against one stored session, and the losing one would keep serving a token the
 * other had already rotated. The temporary session must also never be persisted, and a client
 * configured to hold it would do exactly that. PostgREST asks for nothing more than these three
 * headers, so nothing is being reimplemented here beyond the request itself.
 *
 * Returns the refusal, or null when the grant was issued.
 */
async function requestGrantAs(
  accessToken: string,
  action: StepUpAction,
  grantTokenHash: string,
  tenantId: string
): Promise<StepUpFailure | null> {
  const url = clientEnv.VITE_SUPABASE_URL;
  const key = clientEnv.VITE_SUPABASE_PUBLISHABLE_KEY;

  if (!url || !key) {
    return "unavailable";
  }

  let response: Response;

  try {
    response = await fetch(`${url}/rest/v1/rpc/request_step_up_grant`, {
      method: "POST",
      headers: {
        apikey: key,
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        p_intended_action: action,
        p_grant_token_hash: grantTokenHash,
        p_tenant_id: tenantId
      })
    });
  } catch {
    return "unavailable";
  }

  if (response.ok) {
    return null;
  }

  // PostgREST puts a raised exception's message in `message`, which is where `classifyGrant` reads
  // the wrapper's own code from. A body that is not JSON at all is a refusal this file cannot read,
  // and an unreadable refusal is `unavailable` rather than the nearest named one.
  let body: { message?: string };
  try {
    body = (await response.json()) as { message?: string };
  } catch {
    body = {};
  }

  return classifyGrant(body);
}

/**
 * One grant, proved with the current password, for an account that has no second factor.
 *
 * The sign-in, the user-ID check and the local revoke are `withTemporaryPasswordSession`'s, and the
 * grant is requested inside it with the temporary session's own access token. That is the whole
 * mechanism: the wrapper measures the `amr` claim of the token it is called with, and only a token
 * minted seconds ago carries a `password` entry inside the 300-second window. Requesting it with the
 * primary session's token would be refused as `PORTAL_STEP_UP_PASSWORD_STALE`, which is section
 * 14.3's own point about a modal dialog, enforced by the database.
 *
 * `email` and `userId` come from the caller's own session and never from a typed field.
 * `captchaToken` is the Turnstile token: the 2026-08-30 spike measured that `grant_type=password` is
 * refused without one on a project with CAPTCHA protection enabled, before the password is checked.
 *
 * The temporary session is revoked whatever happens here, including when the grant is refused,
 * because `withTemporaryPasswordSession` revokes in a `finally`.
 */
export async function requestStepUpGrantWithPassword(input: {
  email: string;
  password: string;
  userId: string;
  captchaToken?: string;
  action: StepUpAction;
  tenantId: string;
}): Promise<StepUpResult> {
  const { token, hash } = await mintGrantToken();

  const outcome = await withTemporaryPasswordSession(
    {
      email: input.email,
      password: input.password,
      expectedUserId: input.userId,
      captchaToken: input.captchaToken
    },
    (accessToken) => requestGrantAs(accessToken, input.action, hash, input.tenantId)
  );

  if (!outcome.ok) {
    return { ok: false, reason: REAUTH_AS_STEP_UP[outcome.reason] };
  }

  return outcome.value === null ? { ok: true, token } : { ok: false, reason: outcome.value };
}
